import React, { useCallback, useEffect, useRef, useState } from "react";
import "./styles.css";

const COMPLETE_AT = 0.9;

/**
 * Drag-to-confirm control used for placing bids.
 * Pointer events keep mouse, touch and pen on one code path; the thumb is a
 * real button so the action stays reachable by keyboard.
 */
export default function SlideButton(props) {
  const {
    disabled,
    onEnd,
    label = "Slide right to bid",
    busyLabel = "Placing bid…",
    releaseLabel = "Release to bid",
  } = props;

  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const progressRef = useRef(0);
  const mountedRef = useRef(true);
  const [progress, setProgressState] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const locked = Boolean(disabled) || busy;

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const setProgress = useCallback((value) => {
    const next = Math.min(1, Math.max(0, value));
    progressRef.current = next;
    setProgressState(next);
  }, []);

  const progressFromClientX = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const thumb = track.querySelector(".sab__thumb");
    const thumbWidth = thumb ? thumb.offsetWidth : 56;
    const usable = Math.max(1, rect.width - thumbWidth);
    return (clientX - rect.left - thumbWidth / 2) / usable;
  }, []);

  const complete = useCallback(async () => {
    setBusy(true);
    try {
      if (typeof onEnd === "function") await onEnd();
    } catch (err) {
      // The caller reports its own failure; just release the slider.
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        setProgress(0);
      }
    }
  }, [onEnd, setProgress]);

  const startDrag = (event) => {
    if (locked) return;
    draggingRef.current = true;
    setDragging(true);
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    setProgress(progressFromClientX(event.clientX));
  };

  const moveDrag = (event) => {
    if (!draggingRef.current) return;
    setProgress(progressFromClientX(event.clientX));
  };

  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    if (progressRef.current >= COMPLETE_AT) {
      setProgress(1);
      complete();
      return;
    }
    setProgress(0);
  };

  // A pointer released outside the thumb (or lost to a re-render that disables
  // it) must still settle the slider.
  useEffect(() => {
    const onWindowRelease = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setDragging(false);
      if (progressRef.current >= COMPLETE_AT) {
        setProgress(1);
        complete();
        return;
      }
      setProgress(0);
    };
    window.addEventListener("pointerup", onWindowRelease);
    window.addEventListener("pointercancel", onWindowRelease);
    return () => {
      window.removeEventListener("pointerup", onWindowRelease);
      window.removeEventListener("pointercancel", onWindowRelease);
    };
  }, [complete, setProgress]);

  const onKeyDown = (event) => {
    if (locked) return;
    if (event.key === "ArrowRight") {
      event.preventDefault();
      setProgress(progressRef.current + 0.25);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setProgress(progressRef.current - 0.25);
      return;
    }
    if (event.key === "Enter" || event.key === " " || event.key === "End") {
      event.preventDefault();
      setProgress(1);
      complete();
    }
  };

  const percent = Math.round(progress * 100);
  const text = busy
    ? busyLabel
    : progress >= COMPLETE_AT
    ? releaseLabel
    : label;

  return (
    <div
      className={`sab${locked ? " is-locked" : ""}${busy ? " is-busy" : ""}${
        dragging ? " is-dragging" : ""
      }`}
    >
      <div className="sab__track" ref={trackRef}>
        <div className="sab__fill" style={{ width: `${percent}%` }} />
        <span className="sab__label">{text}</span>
        <button
          type="button"
          className="sab__thumb"
          style={{ left: `calc(${progress} * (100% - var(--sab-thumb)))` }}
          role="slider"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          disabled={locked}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
        >
          <span className="sab__thumb-icon" aria-hidden />
        </button>
      </div>
    </div>
  );
}
